import React, { useEffect, useMemo, useState } from "react";
import {
  useForm,
  FormProvider,
  useFieldArray,
  Controller,
  useFormContext,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  TextField,
  TextAreaField,
  CheckboxGroupField,
  CheckboxField,
  SelectField as UISelectField,
  MultiSelectField as UIMultiSelectField,
  ComboboxField,
} from "../../components/ui/FormControls";
import { ImageDropField } from "../../components/ui/ImageDropField";
import {
  fetchCategories,
  fetchDistrictsByState,
  fetchServicesByCategory,
  fetchStates,
  fetchSubcategoriesByCategory,
  fetchSubscriptionPackages,
  fetchTalukasByDistrict,
  fetchPaymentModes,
} from "../../store/slices/commonApiSlice";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import Loader from "../../components/Loader";
import {
  createShop,
  fetchShopById,
  updateShop,
} from "../../store/slices/shopsSlice";
import { showApiError, showApiSuccess, showToast } from "../../utils/toast";

// ----- Stepper -----
const steps = [
  "Business Info",
  "Services & Gallery",
  "Discount Info",
  "Contact Info",
  "Working Hours",
  // "Review & Submit",
];

const Stepper = ({ currentStep, onStepClick, validatedSteps = [] }) => {
  return (
    <div className="w-full mb-6">
      <ol className="flex items-center overflow-x-auto">
        {steps.map((label, idx) => {
          const isActive = idx === currentStep;
          const isDone = idx < currentStep;
          const isValidated = validatedSteps.includes(idx);
          const isClickable = isDone || isValidated || idx === currentStep;

          return (
            <li key={label} className="flex items-center">
              <div
                onClick={() => isClickable && onStepClick && onStepClick(idx)}
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-colors ${
                  isDone
                    ? "bg-primary text-white cursor-pointer hover:bg-primary-600"
                    : isActive
                    ? "bg-primary-100 text-primary border-2 border-primary"
                    : isValidated
                    ? "bg-green-100 text-green-800 cursor-pointer hover:bg-green-200"
                    : "bg-surface-200 text-text-secondary"
                } ${isClickable ? "cursor-pointer" : "cursor-not-allowed"}`}
              >
                {idx + 1}
              </div>
              <span
                onClick={() => isClickable && onStepClick && onStepClick(idx)}
                className={`ml-2 mr-4 whitespace-nowrap text-sm transition-colors ${
                  isActive ? "text-primary font-medium" : "text-text-secondary"
                } ${
                  isClickable
                    ? "cursor-pointer hover:text-primary"
                    : "cursor-not-allowed"
                }`}
              >
                {label}
              </span>
              {idx < steps.length - 1 && (
                <div className="h-0.5 w-6 sm:w-10 bg-border-medium mx-1 sm:mx-2" />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

// Payment options will be fetched from API

const phoneRegex = /^[0-9]{10}$/;
const pincodeRegex = /^[0-9]{6}$/;
const days = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const stepSchemas = [
  yup.object({
    business_info: yup.object({
      state: yup.string().required("State is required"),
      district: yup.string().required("District is required"),
      mode: yup.string().required("Mode is required"),
      taluka: yup.string().required("Taluk is required"),
      category: yup.string().required("Category is required"),
      sub_categories: yup
        .array()
        .of(yup.string())
        .min(1, "Select at least one sub category")
        .required("Sub categories are required"),
      business_name: yup.string().required("Shop Name is required").max(20, "Business name cannot exceed 20 characters"),
      username: yup
        .string()
        .required("Mobile number is required")
        .matches(/^[0-9]+$/, "Mobile number must contain only digits")
        .min(10, "Mobile number must be exactly 10 digits")
        .max(10, "Mobile number must be exactly 10 digits"),
      about_business: yup.string().required("About business is required"),
    }),
  }),
  // Step 2: Services & Gallery
  yup.object({
    services: yup.array().when("$isEditMode", {
      is: true,
      then: () => yup.array().of(yup.string()).nullable(),
      otherwise: () =>
        yup
          .array()
          .of(yup.string())
          .min(1, "Select at least one service")
          .required("Services are required"),
    }),
    payment_modes: yup
      .array()
      .of(yup.string())
      .min(1, "Select at least one mode of payment")
      .required("Mode of Payment is required"),
    gallery: yup.mixed().when("$isEditMode", {
      is: true,
      then: () =>
        yup
          .mixed()
          .test("fileSize", "Image size must be less than 5MB", (value) => {
            if (!value || value.length === 0) return true;
            return Array.from(value).every((file) => {
              // Skip size check for existing images
              if (file.existing) return true;
              // Check File objects
              if (file instanceof File) return file.size <= 5000000;
              // Check wrapped file objects
              if (file.file) return file.file.size <= 5000000;
              return true;
            });
          })
          .nullable(),
      otherwise: () =>
        yup
          .mixed()
          .test("required", "At least one image is required", (value) => {
            return value && value.length > 0;
          })
          .test("fileSize", "Image size must be less than 5MB", (value) => {
            if (!value) return true;
            return Array.from(value).every((file) => {
              // Check File objects
              if (file instanceof File) return file.size <= 5000000;
              // Check wrapped file objects
              if (file.file) return file.file.size <= 5000000;
              return true;
            });
          })
          .required("Gallery images are required"),
    }),
  }),
  // Step 3: Discount Info
  yup.object({
    discount: yup.object({
      discount_text: yup.string().required("Discount text is required"),
      fixed_discount: yup.string().when("mode", {
        is: "Premium",
        then: () =>
          yup
            .number()
            .typeError("Fixed discount must be a number")
            .required("Fixed discount is required")
            .min(0, "Must be 0 or more")
            .test("non-negative", "Cannot be negative", (value) => value >= 0),
        otherwise: () => yup.string().nullable(),
      }),
      customer_discount: yup.string().when("mode", {
        is: "Premium",
        then: () =>
          yup
            .number()
            .typeError("Customer discount must be a number")
            .required("Customer discount is required")
            .min(0, "Must be 0 or more"),
        otherwise: () => yup.string().nullable(),
      }),
      company_commission: yup.string().when("mode", {
        is: "Premium",
        then: () =>
          yup
            .number()
            .typeError("Company commission must be a number")
            .required("Company commission is required")
            .min(0, "Must be 0 or more"),
        otherwise: () => yup.string().nullable(),
      }),
    }),
  }),
  // Step 4: Contact Info
  yup.object({
    contact: yup.object({
      address: yup.string().required("Address is required"),
      contact_numbers: yup
        .array()
        .of(
          yup
            .string()
            .trim()
            .matches(/^[0-9]*$/, "Contact number must be digits")
        )
        .test(
          "at-least-one",
          "Enter at least one contact number",
          (arr) =>
            (arr || []).filter((v) => (v || "").trim().length > 0).length >= 1
        ),
      whatsapp: yup
        .string()
        .required("WhatsApp number is required")
        .matches(phoneRegex, "WhatsApp number must be 10 digits"),
      email: yup
        .string()
        .required("Email is required")
        .email("Enter a valid email"),
      website: yup.string().nullable().url("Enter a valid URL"),
      pincode: yup
        .string()
        .required("Pincode is required")
        .matches(pincodeRegex, "Pincode must be 6 digits"),
      business_location_map: yup.string().nullable(),
      latitude: yup.number().transform((value, originalValue) => {
        return originalValue === '' ? null : value;
      }).min(-90).max(90).nullable(),
      longitude: yup.number().transform((value, originalValue) => {
        return originalValue === '' ? null : value;
      }).min(-180).max(180).nullable(),
    }),
  }),
  // Step 5: Working Hours (no strict validation on times)
  yup.object({
    working_hours: yup.object().shape(
      days.reduce((acc, day) => {
        acc[day] = yup.array().of(
          yup.object({
            open: yup.string().nullable(),
            close: yup.string().nullable(),
          })
        );
        return acc;
      }, {})
    ),
    flags: yup.object({
      is_trending: yup.boolean().default(false),
      is_recently_added: yup.boolean().default(false),
    }),
  }),
  // Step 6: Review & Submit
  yup.object({}),
];

// ----- Step field lists (for per-step validation trigger) -----
const stepFields = [
  [
    "business_info.state",
    "business_info.district",
    "business_info.taluka",
    "business_info.category",
    "business_info.sub_categories",
    "business_info.mode",
    "business_info.business_name",
    "business_info.username",
    "business_info.about_business",
    "business_info.payment_modes",
  ],
  ["services", "payment_modes", "gallery"],
  [
    "discount.discount_text",
    "discount.fixed_discount",
    "discount.customer_discount",
    "discount.company_commission",
  ],
  [
    "contact.address",
    "contact.contact_numbers",
    "contact.whatsapp",
    "contact.email",
    "contact.website",
    "contact.pincode",
    "contact.business_location_map",
  ],
  [
    "working_hours.monday",
    "working_hours.tuesday",
    "working_hours.wednesday",
    "working_hours.thursday",
    "working_hours.friday",
    "working_hours.saturday",
    "working_hours.sunday",
    "flags.is_trending",
    "flags.is_recently_added",
  ],
  [],
];

// ----- Default values -----
const defaultValues = {
  business_info: {
    state: "",
    district: "",
    taluka: "",
    category: "",
    sub_categories: [],
    business_name: "",
    mode: "",
    username: "",
    about_business: "",
  },
  services: [],
  payment_modes: [],
  gallery: null,
  discount: {
    mode: "",
    discount_text: "",
    fixed_discount: "",
    customer_discount: "",
    company_commission: "",
  },
  contact: {
    address: "",
    contact_numbers: [""],
    whatsapp: "",
    email: "",
    website: "",
    pincode: "",
    business_location_map: "",
    latitude: null,
    longitude: null,
  },
  working_hours: {
    monday: [{ open: "", close: "" }, { open: "", close: "" }],
    tuesday: [{ open: "", close: "" }, { open: "", close: "" }],
    wednesday: [{ open: "", close: "" }, { open: "", close: "" }],
    thursday: [{ open: "", close: "" }, { open: "", close: "" }],
    friday: [{ open: "", close: "" }, { open: "", close: "" }],
    saturday: [{ open: "", close: "" }, { open: "", close: "" }],
    sunday: [{ open: "", close: "" }, { open: "", close: "" }],
  },
  flags: {
    is_trending: false,
    is_recently_added: false,
  },
};

const AddBussinessWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editShopId = searchParams.get("edit");
  const isEditMode = Boolean(editShopId);

  const {
    states,
    districts,
    talukas,
    categories,
    subcategories,
    subscriptionPackages,
    categoryServices,
    paymentModes,
    loading,
  } = useSelector((state) => state.common);
  const { shopCreateStatus, shopCreateError, shopUpdateStatus, shopUpdateError, shop, shopFetchStatus } =
    useSelector((state) => state.shop);

  useEffect(() => {
    dispatch(fetchStates());
    dispatch(fetchCategories());
    dispatch(fetchSubscriptionPackages());
    dispatch(fetchPaymentModes());

    // Fetch shop data if in edit mode
    if (isEditMode && editShopId) {
      dispatch(fetchShopById(editShopId));
    }
  }, [dispatch, isEditMode, editShopId]);
  const [currentStep, setCurrentStep] = useState(0);
  const [validatedSteps, setValidatedSteps] = useState([]);
  const getGoogleMapsUrl = (lat, lng) => {
    return `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
  };
  const methods = useForm({
    mode: "onSubmit",
    defaultValues,
    resolver: yupResolver(stepSchemas[currentStep]),
    context: { isEditMode },
  });

  // Update resolver when step changes
  useEffect(() => {
    methods.clearErrors();
    // Update the form's context for validation
    methods.context = { isEditMode };
  }, [currentStep, isEditMode, methods]);

  // Populate form with shop data when in edit mode
  useEffect(() => {
    if (isEditMode && shop && shopFetchStatus === "success") {
      const formData = {
        business_info: {
          state: shop.state?.uuid || "",
          district: shop.district?.uuid || "",
          taluka: shop.taluka?.uuid || shop.taluka?.uuid || "", // Handle both possible field names
          category: shop.category?.uuid || "",
          sub_categories: shop.subcategories?.map((sub) => sub.uuid) || [],
          business_name: shop.business_name || "",
          mode: shop.subscription_package?.name || shop.mode || "", // Handle both possible field names
          username: shop.username || "",
          about_business: shop.about_business || "",
        },
        services: shop.services?.map((service) => service.uuid) || [], // Keep empty if not in API response
        payment_modes: Array.isArray(shop.payment_modes)
          ? shop.payment_modes
          : [],
        gallery:
          shop.gallery_images?.map((img) => ({
            preview: img.image,
            existing: true,
            uuid: img.uuid,
          })) || null,
        discount: {
          discount_text: shop.active_discount?.discount_text || "",
          fixed_discount: shop.active_discount?.fixed_discount || "",
          customer_discount: shop.active_discount?.customer_discount || "",
          company_commission: shop.active_discount?.company_commission || "",
        },
        contact: {
          address: shop.address || "",
          contact_numbers: Array.isArray(shop.contact_numbers)
            ? shop.contact_numbers
            : shop.contact_numbers
            ? [shop.contact_numbers]
            : [""],
          whatsapp: shop.whatsapp_number || "", // Note: API uses whatsapp_number
          email: shop.email || "",
          website: shop.website || "",
          pincode: shop.pincode?.toString() || "", // Convert to string as form expects string
          business_location_map: shop.business_location_map || "",
          latitude: shop.latitude || null,
          longitude: shop.longitude || null,
        },
        working_hours: shop.business_timings || {
          monday: [{ open: "", close: "" }, { open: "", close: "" }],
          tuesday: [{ open: "", close: "" }, { open: "", close: "" }],
          wednesday: [{ open: "", close: "" }, { open: "", close: "" }],
          thursday: [{ open: "", close: "" }, { open: "", close: "" }],
          friday: [{ open: "", close: "" }, { open: "", close: "" }],
          saturday: [{ open: "", close: "" }, { open: "", close: "" }],
          sunday: [{ open: "", close: "" }, { open: "", close: "" }],
        },
        flags: {
          is_trending: shop.is_trending || false,
          is_recently_added: shop.is_recently_added || false,
        },
      };

      methods.reset(formData);

      // Fetch related data based on shop's location and category
      if (shop.state?.uuid) {
        dispatch(fetchDistrictsByState(shop.state.uuid));
      }
      if (shop.district?.uuid) {
        dispatch(fetchTalukasByDistrict(shop.district.uuid));
      }
      if (shop.category?.uuid) {
        dispatch(fetchSubcategoriesByCategory(shop.category.uuid));
        dispatch(fetchServicesByCategory(shop.category.uuid));
      }

      // Mark all steps as validated in edit mode since data is already present
      if (isEditMode) {
        setValidatedSteps([0, 1, 2, 3, 4]);
      }
    }
  }, [isEditMode, shop, shopFetchStatus, methods, dispatch]);

  const {
    register,
    control,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors },
  } = methods;

  // Handle shop create status
  useEffect(() => {
    if (shopCreateStatus === "success") {
      methods.reset(defaultValues, {
        keepErrors: false,
        keepDirty: false,
        keepTouched: false,
        keepIsSubmitted: false,
        keepSubmitCount: false,
      });
      setCurrentStep(0);
      showApiSuccess("Shop added successfully");
      navigate("/business");
    } else if (shopCreateStatus === "failed") {
      console.log('Shop create failed, error:', shopCreateError);
      
      if (shopCreateError?.data && typeof shopCreateError.data === 'object') {
        const validationErrors = shopCreateError.data;
        console.log('Validation errors:', validationErrors);
        
        // Build error message with all validation errors
        const errorMessages = [];
        Object.keys(validationErrors).forEach((field) => {
          const fieldErrors = validationErrors[field];
          const errorMessage = Array.isArray(fieldErrors) ? fieldErrors[0] : fieldErrors;
          errorMessages.push(errorMessage);
          
          // Also set errors on form fields
          const fieldMapping = {
            'whatsapp_number': 'contact.whatsapp',
            'email': 'contact.email',
            'business_name': 'business_info.business_name',
            'username': 'business_info.username',
            'about_business': 'business_info.about_business',
            'state_id': 'business_info.state',
            'district_id': 'business_info.district',
            'taluka_id': 'business_info.taluka',
            'category_id': 'business_info.category',
            'subscription_package_id': 'business_info.mode',
            'address': 'contact.address',
            'pincode': 'contact.pincode',
            'website': 'contact.website',
          };
          
          const formField = fieldMapping[field] || field;
          methods.setError(formField, {
            type: 'manual',
            message: errorMessage,
          });
        });
        
        // Show all errors in toast
        if (errorMessages.length > 0) {
          const errorText = errorMessages.join('. ');
          console.log('Showing error toast:', errorText);
          showToast.error(errorText);
        } else {
          console.log('No validation errors found, showing general message');
          showToast.error(shopCreateError.message || "Failed to create shop");
        }
      } else {
        console.log('No validation data, showing general error');
        showToast.error(shopCreateError?.message || "Failed to create shop");
      }
    }
  }, [shopCreateStatus, shopCreateError, navigate, methods]);

  // Handle shop update status
  useEffect(() => {
    if (shopUpdateStatus === "success") {
      showApiSuccess("Shop updated successfully");
      navigate("/business");
    } else if (shopUpdateStatus === "failed") {
      console.log('Shop update failed, error:', shopUpdateError);
      
      if (shopUpdateError?.data && typeof shopUpdateError.data === 'object') {
        const validationErrors = shopUpdateError.data;
        console.log('Validation errors:', validationErrors);
        
        // Build error message with all validation errors
        const errorMessages = [];
        Object.keys(validationErrors).forEach((field) => {
          const fieldErrors = validationErrors[field];
          const errorMessage = Array.isArray(fieldErrors) ? fieldErrors[0] : fieldErrors;
          errorMessages.push(errorMessage);
          
          // Also set errors on form fields
          const fieldMapping = {
            'whatsapp_number': 'contact.whatsapp',
            'email': 'contact.email',
            'business_name': 'business_info.business_name',
            'username': 'business_info.username',
            'about_business': 'business_info.about_business',
            'state_id': 'business_info.state',
            'district_id': 'business_info.district',
            'taluka_id': 'business_info.taluka',
            'category_id': 'business_info.category',
            'subscription_package_id': 'business_info.mode',
            'address': 'contact.address',
            'pincode': 'contact.pincode',
            'website': 'contact.website',
          };
          
          const formField = fieldMapping[field] || field;
          methods.setError(formField, {
            type: 'manual',
            message: errorMessage,
          });
        });
        
        // Show all errors in toast
        if (errorMessages.length > 0) {
          const errorText = errorMessages.join('. ');
          console.log('Showing error toast:', errorText);
          showToast.error(errorText);
        } else {
          console.log('No validation errors found, showing general message');
          showToast.error(shopUpdateError.message || "Failed to update shop");
        }
      } else {
        console.log('No validation data, showing general error');
        showToast.error(shopUpdateError?.message || "Failed to update shop");
      }
    }
  }, [shopUpdateStatus, shopUpdateError, navigate, methods]);

  const {
    fields: contact_numbers,
    append: appendContactNumber,
    remove: removeContactNumber,
  } = useFieldArray({
    name: "contact.contact_numbers",
    control,
  });
  useEffect(() => {
    if (contact_numbers.length === 0) {
      appendContactNumber("");
    }
  }, [contact_numbers.length, appendContactNumber]);

  const canGoBack = currentStep > 0;
  const isLastStep = currentStep === steps.length - 1;

  const onNext = async () => {
    const fieldsToValidate = stepFields[currentStep];
    const valid = await trigger(fieldsToValidate);
    if (!valid) return;

    // Mark current step as validated
    setValidatedSteps((prev) => {
      if (!prev.includes(currentStep)) {
        return [...prev, currentStep];
      }
      return prev;
    });

    setCurrentStep((s) => s + 1);
  };

  const onStepClick = async (stepIndex) => {
    // If clicking on a previous step, just go there
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
      return;
    }

    // If clicking on current step, do nothing
    if (stepIndex === currentStep) {
      return;
    }

    // If clicking on a future step, validate current step first
    if (stepIndex > currentStep) {
      const fieldsToValidate = stepFields[currentStep];
      const valid = await trigger(fieldsToValidate);
      if (!valid) return;

      // Mark current step as validated
      setValidatedSteps((prev) => {
        if (!prev.includes(currentStep)) {
          return [...prev, currentStep];
        }
        return prev;
      });

      setCurrentStep(stepIndex);
    }
  };

  const onBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const buildShopFormData = (shopData) => {
    const formData = new FormData();
    formData.append("business_name", shopData.business_info.business_name);
    formData.append("state_id", shopData.business_info.state);
    formData.append("district_id", shopData.business_info.district);
    if (shopData.business_info.taluka) {
      formData.append("taluka_id", shopData.business_info.taluka);
    }
    formData.append("category_id", shopData.business_info.category);
    if (shopData.business_info.mode) {
      formData.append("subscription_package_id", shopData.business_info.mode);
    }
    formData.append("about_business", shopData.business_info.about_business);
    shopData.business_info.sub_categories.forEach((id, i) =>
      formData.append(`sub_categories[${i}]`, id)
    );
    if (shopData.services && shopData.services.length > 0) {
      shopData.services.forEach((id, i) =>
        formData.append(`services[${i}]`, id)
      );
    }

    // Append payment modes as JSON string (backend expects it this way)
    formData.append("payment_modes", JSON.stringify(shopData.payment_modes));
    
    // Format business_timings with shift-wise slots
    const formattedTimings = {};
    Object.keys(shopData.working_hours).forEach((day) => {
      const daySlots = shopData.working_hours[day];
      const validSlots = daySlots.filter(slot => slot.open && slot.close);
      
      if (validSlots.length > 0) {
        formattedTimings[day.charAt(0).toUpperCase() + day.slice(1)] = validSlots;
      } else {
        formattedTimings[day.charAt(0).toUpperCase() + day.slice(1)] = [];
      }
    });
    
    formData.append("business_timings", JSON.stringify(formattedTimings));
    formData.append(
      "contact_numbers",
      JSON.stringify(shopData.contact.contact_numbers)
    );

    formData.append("address", shopData.contact.address);
    formData.append("whatsapp_number", shopData.contact.whatsapp);
    formData.append("email", shopData.contact.email);
    formData.append("website", shopData.contact.website);
    formData.append("pincode", shopData.contact.pincode);
    formData.append("business_location_map", shopData.contact.business_location_map);
    
    // Append latitude and longitude if available
    if (shopData.contact.latitude !== null && shopData.contact.latitude !== undefined) {
      formData.append("latitude", shopData.contact.latitude);
      console.log('✅ Appending latitude to FormData:', shopData.contact.latitude);
    } else {
      console.log('❌ Latitude not appended:', shopData.contact.latitude);
    }
    if (shopData.contact.longitude !== null && shopData.contact.longitude !== undefined) {
      formData.append("longitude", shopData.contact.longitude);
      console.log('✅ Appending longitude to FormData:', shopData.contact.longitude);
    } else {
      console.log('❌ Longitude not appended:', shopData.contact.longitude);
    }
    
    formData.append("username", shopData.business_info.username);
    formData.append("discount_text", shopData.discount.discount_text);
    formData.append("fixed_discount", shopData.discount.fixed_discount || 0);
    formData.append("customer_discount", shopData.discount.customer_discount);
    formData.append("company_commission", shopData.discount.company_commission);

    // Handle gallery images - append new files only
    if (shopData.gallery && shopData.gallery.length > 0) {
      let galleryIndex = 0;
      shopData.gallery.forEach((fileObj) => {
        // Check if it's a File object directly
        if (fileObj instanceof File) {
          formData.append(`gallery[${galleryIndex}]`, fileObj);
          galleryIndex++;
        } 
        // Check if it's wrapped in an object with a file property and not an existing image
        else if (fileObj && typeof fileObj === 'object' && !fileObj.existing) {
          if (fileObj.file) {
            formData.append(`gallery[${galleryIndex}]`, fileObj.file);
            galleryIndex++;
          }
        }
        // Skip existing images - they don't need to be sent
      });
    }

    // Flags
    formData.append("is_trending", shopData.flags.is_trending ? 1 : 0);
    formData.append(
      "is_recently_added",
      shopData.flags.is_recently_added ? 1 : 0
    );

    return formData;
  };
  const onFinalSubmit = (data) => {
    console.log('Form data before building FormData:', data);
    console.log('Gallery data:', data.gallery);
    
    const modeUuid = subscriptionPackages.find(
      (p) => p.name === data.business_info.mode
    )?.uuid;
    const safeData = {
      ...data,
      business_info: {
        ...data.business_info,
        mode: modeUuid || data.business_info.mode, // Keep original if UUID not found
      },
    };
    const formData = buildShopFormData(safeData);

    // Debug: Log FormData contents
    console.log('FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }

    if (isEditMode) {
      dispatch(updateShop({ uuid: editShopId, data: formData }));
    } else {
      dispatch(createShop(formData));
    }
  };

  // ---- Step contents ----
  const Step1 = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* State */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name="business_info.state"
            disabled={!states.length}
            render={({ field }) => (
              <UISelectField
                value={field.value}
                onChange={(val) => {
                  field.onChange(val);
                  dispatch(fetchDistrictsByState(val));
                }}
                options={states.map((s) => ({ label: s.name, value: s.uuid }))}
                placeholder="Select State"
                error={errors?.business_info?.state?.message}
              />
            )}
          />
        </div>

        {/* District */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            District <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name="business_info.district"
            disabled={!districts.length}
            render={({ field }) => (
              <UISelectField
                value={field.value}
                onChange={(val) => {
                  field.onChange(val);
                  dispatch(fetchTalukasByDistrict(val));
                }}
                options={districts.map((s) => ({
                  label: s.name,
                  value: s.uuid,
                }))}
                placeholder="Select District"
                error={errors?.business_info?.district?.message}
              />
            )}
          />
        </div>

        {/* Taluk */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Taluk <span className="text-red-500">*</span>
          </label>

          <Controller
            control={control}
            name="business_info.taluka"
            disabled={!talukas.length}
            render={({ field }) => (
              <UISelectField
                value={field.value}
                onChange={field.onChange}
                options={talukas.map((s) => ({ label: s.name, value: s.uuid }))}
                placeholder="Select Taluk"
                error={errors?.business_info?.taluka?.message}
              />
            )}
          />
        </div>

        {/* Mode */}

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name="business_info.category"
            disabled={!categories.length}
            render={({ field }) => {
              // Find the matching category to construct the proper value format
              const selectedCategory = categories.find(cat => cat.uuid === field.value);
              const displayValue = selectedCategory ? `${selectedCategory.slug}|${selectedCategory.uuid}` : "";
              
              return (
                <UISelectField
                  value={displayValue}
                  onChange={(val) => {
                    if (val) {
                      const values = val.split("|");
                      field.onChange(values[1]); // Store only the UUID
                      dispatch(fetchSubcategoriesByCategory(values[0]));
                      dispatch(fetchServicesByCategory(values[0]));
                    } else {
                      field.onChange("");
                    }
                  }}
                  options={categories.map((s) => ({
                    label: s.name,
                    value: `${s.slug}|${s.uuid}`,
                  }))}
                  placeholder="Select Category"
                  error={errors?.business_info?.category?.message}
                />
              );
            }}
          />
        </div>

        {/* Sub Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sub Category <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name="business_info.sub_categories"
            disabled={!subcategories.length}
            render={({ field }) => (
              <UIMultiSelectField
                value={field.value}
                onChange={field.onChange}
                options={subcategories.map((s) => ({
                  label: s.name,
                  value: s.uuid,
                }))}
                defaultValue={[]}
                placeholder="Select Sub Category"
                error={errors?.business_info?.sub_categories?.message}
              />
            )}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mode <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name="business_info.mode"
            disabled={!subscriptionPackages.length}
            render={({ field }) => (
              <UISelectField
                value={field.value}
                onChange={field.onChange}
                options={subscriptionPackages.map((s) => ({
                  label: s.name,
                  value: s.name,
                }))}
                placeholder="Select Mode"
                error={errors?.business_info?.mode?.message}
              />
            )}
          />
        </div>

        {/* Shop Name */}
        <div>
          <TextField
            label="Business Name"
            required
            placeholder="Your Business Name"
            maxLength={20}
            {...register("business_info.business_name")}
            error={errors?.business_info?.business_name?.message}
          />
        </div>

        {/* Mobile Number (Username) */}
        <div>
          <TextField
            label="Username"
            required
            placeholder="10-digit mobile number"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={10}
            {...register("business_info.username")}
            error={errors?.business_info?.username?.message}
          />
        </div>

        {/* About Business */}
        <div className="md:col-span-2">
          <TextAreaField
            label="About Business"
            required
            rows={3}
            placeholder="Describe your business..."
            {...register("business_info.about_business")}
            error={errors?.business_info?.about_business?.message}
          />
        </div>

        {/* Mode of Payment (checkbox group) */}
      </div>
    );
  };

  const Step2 = () => {
    // Filter only active payment modes
    const activePaymentModes = paymentModes.filter(pm => pm.status);
    
    return (
      <div className="space-y-6">
        <div className="">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mode of Payment <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name="payment_modes"
            defaultValue={[]}
            render={({ field }) => (
              <CheckboxGroupField
                value={field.value}
                onChange={field.onChange}
                options={activePaymentModes.map(pm => ({ label: pm.name, value: pm.name }))}
                error={errors?.payment_modes?.message}
                required
              />
            )}
          />
        </div>
        {/* Services (checkbox group) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Services <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name="services"
            defaultValue={[]}
            render={({ field }) => (
              <ComboboxField
                required
                value={field.value}
                onChange={field.onChange}
                options={categoryServices.map((s) => ({
                  label: s.name,
                  value: s.uuid,
                }))}
                error={errors?.services?.message}
                placeholder="Select or type to add services..."
              />
            )}
          />
        </div>

        {/* Gallery (optional multiple) */}
        <div>
          <Controller
            control={control}
            name="gallery"
            render={({ field }) => (
              <ImageDropField
                label="Gallery Images"
                required
                value={field.value}
                onChange={(files) => field.onChange(files)}
                hint="Drop up to 5 images or click to browse"
                maxFiles={5}
                error={errors?.gallery?.message}
              />
            )}
          />
        </div>
      </div>
    );
  };

  const Step3 = () => {
    const { watch, setValue } = useFormContext();
    const mode = watch("business_info.mode");
    const isPremium = mode === "Premium";
    
    // Watch customer discount and company commission to auto-calculate fixed discount
    const customerDiscount = watch("discount.customer_discount");
    const companyCommission = watch("discount.company_commission");

    useEffect(() => {
      if (isPremium) {
        const customer = parseFloat(customerDiscount) || 0;
        const company = parseFloat(companyCommission) || 0;
        const total = customer + company;
        setValue("discount.fixed_discount", total > 0 ? total : "");
      }
    }, [customerDiscount, companyCommission, isPremium, setValue]);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Discount Text */}
        <div className="md:col-span-2">
          <TextField
            label="Discount Text"
            required
            placeholder="e.g., Flat 10% off on all items"
            maxLength={32}
            {...register("discount.discount_text")}
            error={errors?.discount?.discount_text?.message}
          />
        </div>

        {/* Customer Discount */}
        <div>
          <TextField
            label="Customer Discount"
            type="number"
            min={0}
            step="0.01"
            required={isPremium}
            disabled={!isPremium}
            placeholder="0"
            {...register("discount.customer_discount")}
            error={errors?.discount?.customer_discount?.message}
          />
          {!isPremium && (
            <p className="mt-1 text-xs text-text-secondary">
              Available only in Premium mode
            </p>
          )}
        </div>

        {/* Company Commission - Only show in Premium mode */}
        {isPremium && (
          <div>
            <TextField
              label="Company Commission"
              type="number"
              min={0}
              step="0.01"
              required={isPremium}
              placeholder="0"
              {...register("discount.company_commission")}
              error={errors?.discount?.company_commission?.message}
            />
          </div>
        )}

        {/* Fixed Discount - Auto-calculated */}
        <div className="md:col-span-2">
          <TextField
            label="Fixed Discount (Auto-calculated)"
            type="number"
            min={0}
            step="0.01"
            disabled
            readOnly
            placeholder="0"
            {...register("discount.fixed_discount", {
              setValueAs: (v) => (v === "" ? undefined : parseFloat(v)),
            })}
            error={errors?.discount?.fixed_discount?.message}
          />
          <p className="mt-1 text-xs text-gray-500">
            * Fixed Discount is automatically calculated as Customer Discount + Company Commission
          </p>
        </div>
      </div>
    );
  };

  const Step4 = () => {
    const { setValue } = useFormContext();
    const handleGetLocation = () => {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by your browser");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const mapUrl = getGoogleMapsUrl(latitude, longitude);
          setValue("contact.business_location_map", mapUrl);
          setValue("contact.latitude", latitude);
          setValue("contact.longitude", longitude);
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Unable to retrieve your location. Please check your browser permissions.");
        }
      );
    };
    return (
      <div className="space-y-6">
        {/* Address */}
        <TextAreaField
          label="Address"
          required
          rows={3}
          placeholder="Enter full address"
          {...register("contact.address")}
          error={errors?.contact?.address?.message}
        />

        {/* Contact Numbers (dynamic) */}
        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Numbers <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => {
                appendContactNumber("");
              }}
              className="text-primary text-sm hover:text-primary-600"
            >
              + Add Number
            </button>
          </div>
          <ContactNumbers
            errors={errors}
            register={register}
            contact_numbers={contact_numbers}
            removeContactNumber={removeContactNumber}
            appendContactNumber={appendContactNumber}
          />
          {errors?.contact?.contact_numbers && (
            <p className="text-error text-sm mt-1">
              {errors.contact.contact_numbers.message}
            </p>
          )}
        </div>

        {/* WhatsApp / Email / Website / Pincode */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField
            label="WhatsApp Number"
            required
            placeholder="10-digit number"
            inputMode="numeric"
            pattern="[0-9]*"
            numbersOnly
            maxLength={10}
            {...register("contact.whatsapp")}
            error={errors?.contact?.whatsapp?.message}
          />

          <TextField
            label="Email"
            required
            placeholder="example@domain.com"
            {...register("contact.email")}
            error={errors?.contact?.email?.message}
          />

          <TextField
            label="Website"
            placeholder="https://example.com"
            {...register("contact.website")}
            error={errors?.contact?.website?.message}
          />

          <TextField
            label="Pincode"
            required
            numbersOnly
            placeholder="6-digit pincode"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            {...register("contact.pincode")}
            error={errors?.contact?.pincode?.message}
          />
        </div>

        {/* Map Location (optional) */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Map Location (optional)
            </label>
            <button
              type="button"
              onClick={handleGetLocation}
              className="text-primary text-sm hover:text-primary-600 flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
              Get Current Location
            </button>
          </div>
          <TextField
            placeholder="Google Maps URL or short description"
            {...register("contact.business_location_map")}
          />
        </div>

        {/* Hidden fields for latitude and longitude */}
        <input 
          type="hidden" 
          {...register("contact.latitude", {
            setValueAs: (v) => v === "" || v === null || v === undefined ? null : parseFloat(v)
          })} 
        />
        <input 
          type="hidden" 
          {...register("contact.longitude", {
            setValueAs: (v) => v === "" || v === null || v === undefined ? null : parseFloat(v)
          })} 
        />
      </div>
    );
  };

  const Step5 = () => {
    const days = useMemo(
      () => [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ],
      []
    );

    return (
      <div className="space-y-6">
        <div className="text-sm text-text-secondary mb-4">
          Configure working hours with up to 2 shifts per day. Leave empty if closed.
        </div>
        
        {days.map((d) => (
          <div
            key={d}
            className="border rounded-lg p-4 bg-surface-50"
          >
            <div className="text-sm font-semibold text-text-primary capitalize mb-3">
              {d}
            </div>
            
            {/* Slot 1 */}
            <div className="mb-3">
              <div className="text-xs font-medium text-text-secondary mb-2">
                Slot 1
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  control={control}
                  name={`working_hours.${d}.0.open`}
                  render={({ field }) => (
                    <TextField
                      type="time"
                      placeholder="Open"
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === "" ? "" : e.target.value
                        )
                      }
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`working_hours.${d}.0.close`}
                  render={({ field }) => (
                    <TextField
                      type="time"
                      placeholder="Close"
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === "" ? "" : e.target.value
                        )
                      }
                    />
                  )}
                />
              </div>
            </div>

            {/* Slot 2 */}
            <div>
              <div className="text-xs font-medium text-text-secondary mb-2">
                Slot 2
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  control={control}
                  name={`working_hours.${d}.1.open`}
                  render={({ field }) => (
                    <TextField
                      type="time"
                      placeholder="Open"
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === "" ? "" : e.target.value
                        )
                      }
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`working_hours.${d}.1.close`}
                  render={({ field }) => (
                    <TextField
                      type="time"
                      placeholder="Close"
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === "" ? "" : e.target.value
                        )
                      }
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ))}
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-3 sm:space-y-0 mt-6">
          <Controller
            control={control}
            name="flags.is_trending"
            render={({ field }) => (
              <CheckboxField
                label="Add to Trending"
                checked={Boolean(field.value)}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={control}
            name="flags.is_recently_added"
            render={({ field }) => (
              <CheckboxField
                label="Add to Recently Added"
                checked={Boolean(field.value)}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>
    );
  };

  const Step6 = () => {
    const values = getValues();
    return (
      <div className="space-y-6 text-sm">
        <section>
          <h3 className="text-text-primary font-semibold mb-2">Shop Info</h3>
          <pre className="bg-surface-50 border border-border-light rounded p-3 overflow-auto text-text-secondary">
            {JSON.stringify(values.business_info, null, 2)}
          </pre>
        </section>
        <section>
          <h3 className="text-text-primary font-semibold mb-2">
            Services & Gallery
          </h3>
          <div className="grid grid-cols-1 gap-2">
            <div>
              <span className="font-medium text-text-primary">Services: </span>
              <span className="text-text-secondary">
                {values.services?.join(", ") || "-"}
              </span>
            </div>
            <div>
              <span className="font-medium text-text-primary">
                Gallery Files:{" "}
              </span>
              <span className="text-text-secondary">
                {values.gallery
                  ? Array.from(values.gallery)
                      .map((f) => f.name)
                      .join(", ")
                  : "-"}
              </span>
            </div>
          </div>
        </section>
        <section>
          <h3 className="text-text-primary font-semibold mb-2">
            Discount Info
          </h3>
          <pre className="bg-surface-50 border border-border-light rounded p-3 overflow-auto text-text-secondary">
            {JSON.stringify(values.discount, null, 2)}
          </pre>
        </section>

        <section>
          <h3 className="text-text-primary font-semibold mb-2">
            Working Hours & Flags
          </h3>
          <pre className="bg-surface-50 border border-border-light rounded p-3 overflow-auto text-text-secondary">
            {JSON.stringify(
              { working_hours: values.working_hours, flags: values.flags },
              null,
              2
            )}
          </pre>
        </section>
      </div>
    );
  };

  // Show loader while fetching shop data in edit mode
  if (isEditMode && shopFetchStatus === "loading") {
    return <Loader />;
  }

  // Show error if shop not found in edit mode
  if (isEditMode && shopFetchStatus === "failed") {
    return (
      <div className="max-w-5xl mx-auto theme-card rounded-lg p-6 text-center">
        <h2 className="text-xl font-bold text-red-600 mb-2">Shop Not Found</h2>
        <p className="text-gray-600 mb-4">
          The shop you're trying to edit could not be found.
        </p>
        <button
          onClick={() => navigate("/business")}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Back to Shops
        </button>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      {loading && <Loader />}
      <form
        className="max-w-5xl mx-auto theme-card rounded-lg p-6"
        onSubmit={handleSubmit(onFinalSubmit)}
      >
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? "Edit Shop" : "Add New Business"}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode
              ? "Update shop information"
              : "Fill in the details to add a new Business"}
          </p>
        </div>
        <Stepper
          currentStep={currentStep}
          onStepClick={onStepClick}
          validatedSteps={validatedSteps}
        />

        <div className="mt-4">
          {currentStep === 0 && <Step1 />}
          {currentStep === 1 && <Step2 />}
          {currentStep === 2 && <Step3 />}
          {currentStep === 3 && <Step4 />}
          {currentStep === 4 && <Step5 />}
          {/* {currentStep === 5 && <Step6 />} */}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <div>
            {canGoBack && (
              <button
                type="button"
                onClick={onBack}
                className="theme-button-secondary px-4 py-2 rounded-md transition-colors"
              >
                Back
              </button>
            )}
          </div>

          <div className="space-x-2">
            {!isLastStep ? (
              <button
                type="button"
                onClick={onNext}
                className="theme-button-primary px-4 py-2 rounded-md transition-colors"
              >
                Next
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => navigate("/business")}
                  className="theme-button-secondary px-4 py-2 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-success text-white px-4 py-2 rounded-md hover:bg-success-600 transition-colors"
                >
                  {isEditMode ? "Update Business" : "Save Business"}
                </button>
              </>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

// ---- Small sub-component for dynamic numbers (kept separate for clarity) ----
const ContactNumbers = ({
  errors,
  register,
  contact_numbers,
  removeContactNumber,
  appendContactNumber,
}) => {
  useEffect(() => {
    if (contact_numbers.length === 0) {
      appendContactNumber("");
    }
  }, [contact_numbers.length, appendContactNumber]); // Add proper dependencies
  const handleKeyPress = (event) => {
    // Allow only numbers and control keys (backspace, delete, arrows etc)
    if (!/[0-9]/.test(event.key) && event.key.length === 1) {
      event.preventDefault();
    }
  };
  return (
    <div className="space-y-2">
      {contact_numbers.map((field, index) => (
        <div key={field.id} className="grid grid-cols-2 gap-2 ">
          <TextField
            className="flex-1"
            placeholder="10-digit number"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={10}
            onKeyPress={handleKeyPress}
            {...register(`contact.contact_numbers.${index}`)}
            error={errors?.contact?.contact_numbers?.[index]?.message}
          />
          {contact_numbers.length > 1 && (
            <button
              type="button"
              onClick={() => removeContactNumber(index)}
              className="text-error text-sm hover:text-error-600 underline transition-colors w-[100px] flex items-center justify-center bg-secondary-light rounded-md"
            >
              Remove
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default AddBussinessWizard;
